import PageHeader from './ui/PageHeader';

const PrivacyPolicy = () => {
    return (
        <div className="min-h-screen bg-background text-foreground pt-32 pb-20">
            <PageHeader
                prefix="PRIVACY"
                highlight="POLICY"
                description="Your privacy matters to us."
            />

            <section className="container mx-auto px-4 max-w-4xl">
                <div className="prose prose-invert prose-lg max-w-none">
                    <p className="text-muted-foreground mb-8">
                        <strong>Last Updated:</strong> December 2024
                    </p>

                    <div className="space-y-8">
                        <section>
                            <h2 className="text-2xl font-bold text-foreground mb-4">1. Introduction</h2>
                            <p className="text-muted-foreground leading-relaxed">
                                Antz AI ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our AI-powered enterprise automation services, website, and related applications.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-foreground mb-4">2. Information We Collect</h2>
                            <h3 className="text-xl font-semibold text-foreground mb-3">2.1 Information You Provide</h3>
                            <ul className="list-disc list-inside text-muted-foreground space-y-2 mb-4">
                                <li>Contact information (name, email address, phone number)</li>
                                <li>Company information (company name, job title, industry)</li>
                                <li>Account credentials</li>
                                <li>Communications you send to us</li>
                                <li>Data you upload or process through our AI services</li>
                            </ul>

                            <h3 className="text-xl font-semibold text-foreground mb-3">2.2 Automatically Collected Information</h3>
                            <ul className="list-disc list-inside text-muted-foreground space-y-2">
                                <li>Device and browser information</li>
                                <li>IP address and location data</li>
                                <li>Usage data and analytics</li>
                                <li>Cookies and similar tracking technologies</li>
                            </ul>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-foreground mb-4">3. How We Use Your Information</h2>
                            <p className="text-muted-foreground leading-relaxed mb-4">We use the information we collect to:</p>
                            <ul className="list-disc list-inside text-muted-foreground space-y-2">
                                <li>Provide, maintain, and improve our AI services</li>
                                <li>Process transactions and send related information</li>
                                <li>Send promotional communications (with your consent)</li>
                                <li>Respond to your comments, questions, and requests</li>
                                <li>Monitor and analyze trends, usage, and activities</li>
                                <li>Detect, investigate, and prevent security incidents</li>
                                <li>Comply with legal obligations</li>
                            </ul>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-foreground mb-4">4. Data Security</h2>
                            <p className="text-muted-foreground leading-relaxed">
                                We implement industry-standard security measures to protect your data, including encryption in transit and at rest, access controls, regular security audits, and secure data centers. However, no method of transmission over the Internet is 100% secure, and we cannot guarantee absolute security.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-foreground mb-4">5. Data Sharing and Disclosure</h2>
                            <p className="text-muted-foreground leading-relaxed mb-4">We may share your information with:</p>
                            <ul className="list-disc list-inside text-muted-foreground space-y-2">
                                <li><strong>Service Providers:</strong> Third parties that help us operate our business</li>
                                <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
                                <li><strong>Business Transfers:</strong> In connection with mergers, acquisitions, or asset sales</li>
                                <li><strong>With Your Consent:</strong> When you have given us permission</li>
                            </ul>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-foreground mb-4">6. Your Rights and Choices</h2>
                            <p className="text-muted-foreground leading-relaxed mb-4">Depending on your location, you may have the right to:</p>
                            <ul className="list-disc list-inside text-muted-foreground space-y-2">
                                <li>Access, correct, or delete your personal data</li>
                                <li>Object to or restrict processing</li>
                                <li>Data portability</li>
                                <li>Withdraw consent</li>
                                <li>Opt-out of marketing communications</li>
                            </ul>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-foreground mb-4">7. Data Retention</h2>
                            <p className="text-muted-foreground leading-relaxed">
                                We retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-foreground mb-4">8. International Data Transfers</h2>
                            <p className="text-muted-foreground leading-relaxed">
                                Your information may be transferred to and processed in countries other than your country of residence. We ensure appropriate safeguards are in place to protect your data in accordance with applicable laws.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-foreground mb-4">9. Children's Privacy</h2>
                            <p className="text-muted-foreground leading-relaxed">
                                Our services are not directed to individuals under 18. We do not knowingly collect personal information from children. If you believe we have collected information from a child, please contact us immediately.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-foreground mb-4">10. Changes to This Policy</h2>
                            <p className="text-muted-foreground leading-relaxed">
                                We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-foreground mb-4">11. Contact Us</h2>
                            <p className="text-muted-foreground leading-relaxed">
                                If you have any questions about this Privacy Policy, please contact us at:
                            </p>
                            <div className="mt-4 text-muted-foreground">
                                <p><strong>Email:</strong> info@antz.ai</p>
                                <p><strong>Location:</strong> Hyderabad, India</p>
                            </div>
                        </section>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default PrivacyPolicy;
